import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';

import { ApplicationConfigService } from '../config/application-config.service';
import { Login } from '../models/login.model';
import { Account } from './account.model';

type JwtToken = {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  uid: string;
  email: string;
  group: string[];
  session_state?: string;
};

@Injectable({ providedIn: 'root' })
export class AuthServerProvider {
  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private applicationConfigService: ApplicationConfigService
  ) {}

  getToken() {
    const tokenInLocalStorage: string | null =
      this.localStorageService.retrieve('authenticationToken');
    const tokenInSessionStorage: string | null =
      this.sessionStorageService.retrieve('authenticationToken');
    return tokenInLocalStorage ?? tokenInSessionStorage ?? null;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  login(credentials: Login): Promise<Account | null> {
    /** TBD temporary fix */
    this.localStorageService.clear('authenticationToken');
    this.sessionStorageService.clear('authenticationToken');
    /** TBD temporary fix */

    return new Promise<Account>((resolve) => {
      this.http
        .post<JwtToken>(
          this.applicationConfigService.getEndpointFor(
            'api/login/authenticate'
          ),
          credentials
        )
        .subscribe((response) => {
          resolve(
            this.authenticateSuccess(response, credentials.rememberMe || false)
          );
        });
    });
  }
  loginWithOtpSuccess(token: string, email: string, roles?: string[]) {
  this.localStorageService.store('authenticationToken', token);
  this.localStorageService.store('email', email);
  if (roles) this.localStorageService.store('USER_ROLE', roles);
}


  refreshToken(): Observable<Account> {
    let data = {
      refreshToken:
        this.localStorageService.retrieve('refreshToken') ??
        this.sessionStorageService.retrieve('refreshToken'),
    };

    return this.http
      .post<JwtToken>(
        this.applicationConfigService.getEndpointFor('api/refreshToken'),
        data
      )
      .pipe(map((response) => this.authenticateSuccess(response, true, true)));
  }

  changePassword(password: string): Observable<HttpResponse<any>> {
    let userName =
      this.localStorageService.retrieve('email') ??
      this.sessionStorageService.retrieve('email');
    let login: Login = {
      userName,
      password,
    };
    return this.http.post<any>(
      this.applicationConfigService.getJpaMicroServiceEndpointFor(
        'api/changePassword'
      ),
      login,
      { observe: 'response' }
    );
  }

  forgotPassword(credentials: Login): Observable<any> {
    return this.http.post(
      this.applicationConfigService.getEndpointFor('api/forgotPassword'),
      credentials,
      { responseType: 'text' }
    );
  }

  secretTokenAuthentication(secretToken: string): Observable<Account> {
    let data = {
      secretToken: encodeURIComponent(secretToken),
    };
    return this.http
      .post<JwtToken>(
        this.applicationConfigService.getEndpointFor(
          'api/secret-token-authentication'
        ),
        data
      )
      .pipe(map((response) => this.authenticateSuccess(response, true, true)));
  }

  logout(): Observable<void> {
    return new Observable((observer) => {
      this.stopRefreshTokenTimer();
      this.localStorageService.clear('authenticationToken');
      this.sessionStorageService.clear('authenticationToken');

      this.localStorageService.clear('refreshToken');
      this.sessionStorageService.clear('refreshToken');
      observer.complete();
    });
  }

  private authenticateSuccess(
    response: JwtToken,
    rememberMe: boolean,
    refresh = false
  ): Account {
    let account: Account = new Account(
      true, // activated
      response.group, // authorities
      response.email, // email
      null, // firstName
      'en', // langKey
      null, // lastName
      response.uid, // login
      null // imageUrl
    );
    const jwt = response.access_token;
    const idToken = response.id_token;
    const refreshToken = response.refresh_token;
    this.startRefreshTokenTimer(response);
    const userId = response.uid;
    const email = response.email;
    const userRole = response.group;
    const sessionState = response.session_state;

    /** TBD temporary fix */
    this.localStorageService.clear('authenticationToken');
    this.sessionStorageService.clear('authenticationToken');

    this.localStorageService.clear('refreshToken');
    this.sessionStorageService.clear('refreshToken');
    /** TBD temporary fix */

    if (rememberMe) {
      this.localStorageService.store('authenticationToken', jwt);
      this.sessionStorageService.clear('authenticationToken');

      this.localStorageService.store('refreshToken', refreshToken);
      this.sessionStorageService.clear('refreshToken');

      this.localStorageService.store('userId', userId);
      this.sessionStorageService.clear('userId');

      this.localStorageService.store('email', email);
      this.sessionStorageService.clear('email');

      this.localStorageService.store('USER_ROLE', userRole);
      this.sessionStorageService.clear('USER_ROLE');

      this.localStorageService.store('sessionState', sessionState);
      this.sessionStorageService.clear('sessionState');
    } else {
      this.sessionStorageService.store('authenticationToken', jwt);
      this.localStorageService.clear('authenticationToken');

      this.sessionStorageService.store('refreshToken', refreshToken);
      this.localStorageService.clear('refreshToken');

      this.sessionStorageService.store('userId', userId);
      this.localStorageService.clear('userId');

      this.sessionStorageService.store('email', email);
      this.localStorageService.clear('email');

      this.sessionStorageService.store('USER_ROLE', userRole);
      this.localStorageService.clear('USER_ROLE');

      this.sessionStorageService.store('sessionState', sessionState);
      this.localStorageService.clear('sessionState');
    }
    // this.fbAuthService.loginWithToken(idToken);

    // Return null or an Account object if available
    return account;
  }

  private refreshTokenTimeout: any;

  private startRefreshTokenTimer(response: any) {
    // parse json object from base64 encoded jwt token
    const jwtToken = JSON.parse(atob(response.access_token.split('.')[1]));

    // set a timeout to refresh the token a minute before it expires
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 5000;
    this.refreshTokenTimeout = setTimeout(
      () => this.refreshToken().subscribe(),
      timeout
    );
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
}
